import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  isAbortError,
  parseWarehouseError,
  warehouseApi,
} from '../api/warehouse.api';
import {
  type BulkPickingOutcome,
  classifySaveError,
  draftFromDetail,
  emptyDraft,
  isItemDirty,
  type ItemDraft,
  type ItemState,
  nextPendingIndex,
  type SaveKind,
  savePlan,
  splitPickerError,
  withSavedDetail,
} from '../components/bulkPicking';
import {
  addRowFor,
  type EditRow,
  mapServerErrors,
  toPayload,
} from '../components/pickingEdit';
import type {
  GoodsIssueDetail,
  GoodsIssueSummary,
  OutboundOrder,
} from '../types';
import { fieldErrorsOf } from './useServerErrors';

/**
 * State and requests of the bulk picking editor: every picking is its own card
 * in one scrolling list. Everything is keyed by the goods issue id: an item's
 * detail, candidates, draft and save outcome never touch another item's, and
 * each request carries the version (`updatedAt`) its own item was loaded with.
 * Requests are abortable; an item loads once its card comes near the view
 * (`activate`) and a reload only resets that item.
 */
export const useBulkPicking = (items: readonly GoodsIssueSummary[]) => {
  const ids = useMemo(() => items.map((g) => g.id), [items]);
  const [drafts, setDrafts] = useState<Record<string, ItemDraft>>({});
  /** Source of truth for async handlers (state lags a render behind). */
  const draftsRef = useRef<Record<string, ItemDraft>>({});
  /** The one save in flight (saves run one at a time). */
  const [busy, setBusy] = useState<{ id: string; kind: SaveKind } | null>(
    null
  );
  /** Cards that came near the view: they, and only they, load. */
  const [active, setActive] = useState<ReadonlySet<string>>(() => new Set());
  const busyRef = useRef(false);
  /** Names saved in this session, latest first. */
  const [usedPickers, setUsedPickers] = useState<string[]>([]);
  const inflight = useRef(new Map<string, AbortController>());

  const patch = useCallback((id: string, fn: (d: ItemDraft) => ItemDraft) => {
    const next = {
      ...draftsRef.current,
      [id]: fn(draftsRef.current[id] ?? emptyDraft()),
    };
    draftsRef.current = next;
    setDrafts(next);
  }, []);

  const abortPrefix = useCallback((prefix: string) => {
    inflight.current.forEach((c, key) => {
      if (key.startsWith(prefix)) {
        c.abort();
        inflight.current.delete(key);
      }
    });
  }, []);

  const activate = useCallback((id: string) => {
    setActive((prev) => (prev.has(id) ? prev : new Set(prev).add(id)));
  }, []);

  /* ------------------------------ loading ------------------------------- */

  const loadDetail = useCallback(
    (id: string) => {
      const key = `d:${id}`;
      if (inflight.current.has(key)) return;
      const controller = new AbortController();
      inflight.current.set(key, controller);
      warehouseApi
        .getGoodsIssue(id, { signal: controller.signal })
        .then((detail) => {
          if (controller.signal.aborted) return;
          patch(id, (d) => draftFromDetail(d, detail));
        })
        .catch((err) => {
          if (controller.signal.aborted || isAbortError(err)) return;
          patch(id, (d) => ({
            ...d,
            loadError: parseWarehouseError(err).message,
          }));
        })
        .finally(() => {
          if (inflight.current.get(key) === controller) {
            inflight.current.delete(key);
          }
        });
    },
    [patch]
  );

  const loadSources = useCallback(
    (id: string, sku: string) => {
      const key = `s:${id}:${sku}`;
      if (inflight.current.has(key)) return;
      const controller = new AbortController();
      inflight.current.set(key, controller);
      warehouseApi
        .listGoodsIssueSources(id, { sku }, { signal: controller.signal })
        .then((data) => {
          if (controller.signal.aborted) return;
          patch(id, (d) => ({
            ...d,
            sources: { ...d.sources, [sku]: { status: 'ready', data } },
          }));
        })
        .catch((err) => {
          if (controller.signal.aborted || isAbortError(err)) return;
          patch(id, (d) => ({
            ...d,
            sources: {
              ...d.sources,
              [sku]: {
                status: 'error',
                message: parseWarehouseError(err).message,
              },
            },
          }));
        })
        .finally(() => {
          if (inflight.current.get(key) === controller) {
            inflight.current.delete(key);
          }
        });
    },
    [patch]
  );

  // An active card loads its picking once …
  const detailKey = ids
    .filter((id) => active.has(id) && !drafts[id]?.detail && !drafts[id]?.loadError)
    .join('\u0001');
  useEffect(() => {
    if (detailKey) detailKey.split('\u0001').forEach((id) => loadDetail(id));
  }, [detailKey, loadDetail]);

  // … and then the candidates of each of its SKUs (once each).
  const sourceKey = ids
    .flatMap((id) => {
      const d = drafts[id];
      if (!active.has(id) || !d?.detail) return [];
      return d.ordered
        .map((o) => o.sku)
        .filter((sku) => !d.sources[sku])
        .map((sku) => `${id}\u0002${sku}`);
    })
    .join('\u0001');
  useEffect(() => {
    if (!sourceKey) return;
    sourceKey.split('\u0001').forEach((k) => {
      const [id, sku] = k.split('\u0002');
      loadSources(id, sku);
    });
  }, [sourceKey, loadSources]);

  // Leaving drops whatever is still in flight.
  useEffect(() => {
    const live = inflight.current;
    return () => {
      live.forEach((c) => c.abort());
      live.clear();
    };
  }, []);

  /** Ask again (the item changed on the server / stock moved). */
  const reload = useCallback(
    (id: string) => {
      inflight.current.get(`d:${id}`)?.abort();
      inflight.current.delete(`d:${id}`);
      abortPrefix(`s:${id}:`);
      patch(id, (d) => ({ ...d, detail: null, loadError: null, sources: {} }));
    },
    [abortPrefix, patch]
  );
  /** The order behind an item was edited ("Ubah" penerima, alamat, jarak):
   *  swap it into the loaded detail — the picking draft is left alone. */
  const updateOrder = useCallback(
    (id: string, order: OutboundOrder) =>
      patch(id, (d) =>
        d.detail ? { ...d, detail: { ...d.detail, outboundOrder: order } } : d
      ),
    [patch]
  );
  const retryLoad = useCallback(
    (id: string) => patch(id, (d) => ({ ...d, loadError: null })),
    [patch]
  );
  const retrySources = useCallback(
    (id: string, sku: string) =>
      patch(id, (d) => {
        const sources = { ...d.sources };
        delete sources[sku];
        return { ...d, sources };
      }),
    [patch]
  );

  /* ------------------------------ editing ------------------------------- */

  const updateRow = useCallback(
    (id: string, key: number, edit: (r: EditRow) => EditRow) =>
      patch(id, (d) => {
        const serverErrors = new Map(d.serverErrors);
        serverErrors.delete(key);
        return {
          ...d,
          rows: d.rows.map((r) => (r.key === key ? edit(r) : r)),
          serverErrors,
          formErrors: [],
        };
      }),
    [patch]
  );
  const addLocation = useCallback(
    (id: string, sku: string, remaining: number) =>
      patch(id, (d) => ({
        ...d,
        rows: addRowFor(d.rows, sku, remaining),
        formErrors: [],
      })),
    [patch]
  );
  const removeRow = useCallback(
    (id: string, key: number) =>
      patch(id, (d) => {
        const serverErrors = new Map(d.serverErrors);
        serverErrors.delete(key);
        return {
          ...d,
          rows:
            d.rows.length > 1 ? d.rows.filter((r) => r.key !== key) : d.rows,
          serverErrors,
          formErrors: [],
        };
      }),
    [patch]
  );
  const setPicker = useCallback(
    (id: string, picker: string) =>
      patch(id, (d) => ({ ...d, picker, pickerError: null })),
    [patch]
  );

  /* ------------------------------- states ------------------------------- */

  const states = ids.map((id) => drafts[id]?.state ?? 'pending');

  /** The next picking still to do after `id` (wrapping), or null. */
  const nextPendingAfter = useCallback(
    (id: string): string | null => {
      const i = nextPendingIndex(
        ids.map((x) => draftsRef.current[x]?.state ?? 'pending'),
        ids.indexOf(id)
      );
      return i === null ? null : ids[i];
    },
    [ids]
  );

  /* ------------------------------- saving ------------------------------- */

  const failWith = useCallback(
    (id: string, err: unknown, sent: readonly EditRow[]) => {
      const e = parseWarehouseError(err);
      const kind = classifySaveError(e);
      if (kind === 'stale') {
        // Not saved; the draft stays in memory until the user reloads.
        patch(id, (d) => ({ ...d, state: 'stale', message: e.message }));
        return;
      }
      if (kind === 'failed') {
        patch(id, (d) => ({ ...d, state: 'failed', message: e.message }));
        return;
      }
      const { picker, rest } = splitPickerError(fieldErrorsOf(err).list);
      const mapped = mapServerErrors(rest, sent);
      const form = [...mapped.form];
      if (!picker && !mapped.byKey.size && !form.length) form.push(e.message);
      patch(id, (d) => ({
        ...d,
        serverErrors: mapped.byKey,
        formErrors: form,
        pickerError: picker,
      }));
      if (kind === 'insufficient') {
        // Stock moved: show fresh availability.
        abortPrefix(`s:${id}:`);
        patch(id, (d) => ({ ...d, sources: {} }));
      }
    },
    [abortPrefix, patch]
  );

  /** Save one picking; resolves to how it ended (null = not saved). */
  const save = useCallback(
    async (id: string, kind: SaveKind): Promise<ItemState | null> => {
      if (busyRef.current) return null;
      const d = draftsRef.current[id];
      if (!d?.detail) return null;
      const picker = d.picker.trim();
      const plan = savePlan(kind, d, d.detail.goodsIssue.status);
      if ((kind === 'ready' || plan.setPicker) && !picker) {
        patch(id, (x) => ({ ...x, pickerError: 'Picker wajib diisi.' }));
        return null;
      }
      if (plan.nothing) return null;
      busyRef.current = true;
      setBusy({ id, kind });
      patch(id, (x) => ({
        ...x,
        pickerError: null,
        serverErrors: new Map(),
        formErrors: [],
      }));
      let detail: GoodsIssueDetail = d.detail;
      const sent = d.rows;
      const take = (res: { detail?: GoodsIssueDetail } | undefined) => {
        if (!res?.detail) return;
        detail = res.detail;
        const fresh = res.detail;
        patch(id, (x) => withSavedDetail(x, fresh));
      };
      try {
        if (plan.putLines) {
          take(
            await warehouseApi.updateGoodsIssueLines(id, {
              ...toPayload(sent),
              expectedUpdatedAt: detail.goodsIssue.updatedAt,
            })
          );
        }
        if (plan.setPicker) {
          take(
            await warehouseApi.setPicker(
              id,
              picker,
              detail.goodsIssue.updatedAt
            )
          );
        }
        if (plan.ready) {
          take(
            await warehouseApi.readyToPickup(
              id,
              picker,
              detail.goodsIssue.updatedAt
            )
          );
        }
        const state: ItemState = plan.ready ? 'ready' : 'saved';
        patch(id, (x) => ({ ...x, state, message: null, picker }));
        if (picker) {
          setUsedPickers((prev) => [
            picker,
            ...prev.filter((n) => n !== picker),
          ]);
        }
        return state;
      } catch (err) {
        if (!isAbortError(err)) failWith(id, err, sent);
        return null;
      } finally {
        busyRef.current = false;
        setBusy(null);
      }
    },
    [failWith, patch]
  );

  /* ------------------------------- derived ------------------------------ */

  const outcomes = useCallback(
    (): BulkPickingOutcome[] =>
      ids.map((id) => ({
        id,
        state: draftsRef.current[id]?.state ?? 'pending',
      })),
    [ids]
  );
  const anyDirty = ids.some((id) => {
    const d = drafts[id];
    return !!d && d.state !== 'ready' && isItemDirty(d);
  });

  return {
    ids,
    drafts,
    states,
    busy,
    usedPickers,
    anyDirty,
    activate,
    nextPendingAfter,
    save,
    reload,
    updateOrder,
    retryLoad,
    retrySources,
    updateRow,
    addLocation,
    removeRow,
    setPicker,
    outcomes,
  };
};
